import { AppId, FCX, ID, ProductLogo, SelectSingle } from "@datassential/platform-ui-lib";
import { useFastestGrowingKeywords } from "@apiHooks/useFastestGrowingKeywords";
import { useEffect, useMemo, useState } from "react";
import './FastestGrowing.scss';
import { routeNames } from "@core/routeNames";
import { Link } from "react-router-dom";
import Widget from "@components/Widget/Widget";

const GrowthItem: FCX<{
    name: string;
    growthPct: number;
    index: number;
}> = ({ name, growthPct, index }) => {
    const encodedName = encodeURIComponent(name || '');

    return (
        <div className="FastestGrowing__item">
            <div className="FastestGrowing__item-content">
                <div className="FastestGrowing__item-sr">{index + 1}.</div>
                <Link
                    className="FastestGrowing__item-title"
                    to={{
                        pathname: routeNames.food,
                        search: `?q=${encodedName}`
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`linkMacsFastestGrowingItem-${index}`}
                >
                    {name}
                </Link>
                <div
                    className="FastestGrowing__item-growth"
                    data-testid={`textMacsFastestGrowingItemGrowth-${index}`}
                >
                    {growthPct > 0 ? '+' : ''}{Math.round(growthPct)}%
                </div>
            </div>
        </div>
    );
};

const ItemsGroupHeader: FCX = () => {
    return (
        <div className="FastestGrowing__item FastestGrowing__item--header">
            <div className="FastestGrowing__item-content">
                <div className="FastestGrowing__item-sr"/>
                <div className="FastestGrowing__item-title"/>
                <div className="FastestGrowing__item-growth FastestGrowing__item-growth-label">
                    % P12M<br/>Growth
                </div>
            </div>
        </div>
    );
};

const FastestGrowing: FCX = () => {
    const { data, isLoading } = useFastestGrowingKeywords();
    const [selectedMacId, setSelectedMacId] = useState<ID | null>(null);

    const options = useMemo(
        () => data.items.map(item => ({
            id: item.macId as ID,
            title: item.macName ?? '',
        })),
        [data.items]
    );

    const selectedItems = useMemo(
        () => {
            const selected = data.items.find(item => item.macId === selectedMacId);
            return selected?.items ?? [];
        },
        [data.items, selectedMacId]
    );

    useEffect(() => {
        if (options.length > 0 && selectedMacId === null) {
            setSelectedMacId(options[0].id);
        }
    }, [options, selectedMacId]);

    return (
        <Widget
            className="FastestGrowing"
            testId="blockMacsFastestGrowing"
            headerContent={(
                <div className="FastestGrowing__header-title" data-testid="textMacsFastestGrowingTitle">
                    FASTEST GROWING IN
                    {!isLoading && (
                        <SelectSingle
                            className="FastestGrowing__select appears-right"
                            value={selectedMacId}
                            setValue={setSelectedMacId}
                            options={options}
                            testId="selectMacsFastestGrowingMac"
                        />
                    )}
                </div>
            )}
        >
            {isLoading && (
                <div className="FastestGrowing__loading">
                    <div className="TrendingNow__spinner">
                        <ProductLogo appId={AppId.Snap} type="regular"/>
                    </div>
                </div>
            )}
            {!isLoading && (
                <div className="FastestGrowing__content appears" data-testid="blockMacsFastestGrowingContent">
                    <div className="FastestGrowing__cols">
                        <div className="FastestGrowing__col">
                            <div className="FastestGrowing__col-content">
                                <ItemsGroupHeader/>
                                {selectedItems.slice(0, 5).map((item, index) => (
                                    <GrowthItem
                                        key={item.wordId ?? index}
                                        name={item.wordName ?? ''}
                                        growthPct={item.oneYearGrowth ?? 0}
                                        index={index}
                                    />
                                ))}
                            </div>
                        </div>
                        <div className="FastestGrowing__col">
                            <div className="FastestGrowing__col-content">
                                <ItemsGroupHeader/>
                                {selectedItems.slice(5, 10).map((item, index) => (
                                    <GrowthItem
                                        key={item.wordId ?? index + 5}
                                        name={item.wordName ?? ''}
                                        growthPct={item.oneYearGrowth ?? 0}
                                        index={index + 5}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Widget>
    );
};

export default FastestGrowing;
